<template>
  <div class="group-card" @click="handleClick">
    <div class="group-card-header">
      <h3>{{ group.name }}</h3>
      <div class="group-badges">
        <span v-if="group.is_personal" class="badge badge-personal">
          <i class="icon-lock"></i> Personal
        </span>
        <span v-if="isOwner" class="badge badge-owner">
          <i class="icon-crown"></i> Owner
        </span>
      </div>
    </div>

    <p class="group-description">
      {{ group.description || 'No description provided' }}
    </p>

    <div class="group-stats">
      <div class="stat">
        <i class="icon-users"></i>
        <span>{{ members.length || 0 }} members</span>
      </div>
      <div class="stat">
        <i class="icon-calendar"></i>
        <span>Created {{ formatDate(group.created_at) }}</span>
      </div>
    </div>

    <div class="group-actions" v-if="showActions">
      <slot name="actions">
        <button @click.stop="$emit('view', group)" class="btn btn-sm btn-primary">
          View Details
        </button>
        <button v-if="canEdit" @click.stop="$emit('edit', group)" class="btn btn-sm btn-secondary">
          Edit
        </button>
      </slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { Group, GroupMember } from '../../types/group';
import { useAuthStore } from '../../stores/auth';

const props = defineProps<{
  group: Group;
  members: readonly GroupMember[];
  showActions?: boolean;
  canEdit?: boolean;
  clickable?: boolean;
}>();

const emit = defineEmits<{
  click: [group: Group];
  view: [group: Group];
  edit: [group: Group];
}>();

const authStore = useAuthStore();

const isOwner = computed(() => {
  return authStore.currentUser?.id === props.group.owner_id;
});

const handleClick = () => {
  if (props.clickable !== false) {
    emit('click', props.group);
  }
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - date.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};
</script>

<style scoped>
.group-card {
  background: var(--color-neutral-white);
  border-radius: var(--radius-lg);
  padding: var(--spacing-6);
  box-shadow: var(--shadow);
  border: 1px solid var(--color-primary-100);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  cursor: pointer;
}

.group-card:hover {
  box-shadow: var(--shadow-lg);
  transform: translateY(-2px);
  border-color: var(--color-primary-200);
}

.group-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, var(--color-primary-400), var(--color-secondary-400));
  transform: scaleX(0);
  transition: transform 0.4s ease-out;
}

.group-card:hover::before {
  transform: scaleX(1);
}

.group-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--spacing-4);
}

.group-card-header h3 {
  color: var(--color-neutral-dark-gray);
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
}

.group-badges {
  display: flex;
  gap: var(--spacing-2);
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-1);
  padding: var(--spacing-1) var(--spacing-3);
  border-radius: var(--radius-2xl);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  text-transform: uppercase;
}

.badge-personal {
  background: var(--color-primary-50);
  color: var(--color-primary-700);
}

.badge-owner {
  background: var(--color-secondary-50);
  color: var(--color-secondary-700);
}

.group-description {
  color: #6b7280;
  margin: 0 0 var(--spacing-4) 0;
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.group-stats {
  display: flex;
  gap: var(--spacing-6);
  padding-top: var(--spacing-4);
  border-top: 1px solid var(--color-primary-100);
}

.stat {
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  color: #9ca3af;
  font-size: var(--font-size-sm);
}

.stat i {
  font-size: 1rem;
  color: #6b7280;
}

.group-actions {
  display: flex;
  gap: var(--spacing-3);
  margin-top: var(--spacing-4);
}
</style>
